import { Printer, X } from "lucide-react";
import Portal from "@/components/Portal";
import { BrandLogo } from "@/components/BrandLogo";

/**
 * PrescriptionSlip — printable takeaway for the patient.
 * Shows patient info, diagnosis, prescription table with bilingual (Malayalam + English)
 * labels for common dosing terms, and doctor signature block.
 *
 * Uses `window.print()` with @media print rules so the slip prints on its own
 * without the surrounding UI.
 */

const MAL = {
  Morning: "രാവിലെ",
  Afternoon: "ഉച്ചയ്ക്ക്",
  Evening: "വൈകുന്നേരം",
  "Night / Bedtime": "രാത്രി / കിടക്കുന്നതിന് മുമ്പ്",
  "Morning & Evening": "രാവിലെയും വൈകുന്നേരവും",
  "Twice daily": "രണ്ട് നേരം",
  "Thrice daily": "മൂന്ന് നേരം",
  "Four times a day": "നാല് നേരം",
  "Before food": "ഭക്ഷണത്തിന് മുമ്പ്",
  "After food": "ഭക്ഷണത്തിന് ശേഷം",
  "Empty stomach": "വെറും വയറ്റിൽ",
  SOS: "ആവശ്യമെങ്കിൽ മാത്രം",
  "As directed": "ഡോക്ടറുടെ നിർദ്ദേശപ്രകാരം",
};

const bilingualFreq = (f) => (f && MAL[f] ? `${f} · ${MAL[f]}` : f || "—");

export default function PrescriptionSlip({ patient, branch, onClose }) {
  const rx = patient?.consultation?.prescription?.items || [];
  const dx = patient?.consultation?.diagnosis_text || "";
  const doctor = patient?.assigned_doctor_name || "—";
  const date = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-center justify-center p-4 print:bg-white print:p-0"
        onClick={onClose}
        data-testid="rx-slip"
      >
        <div
          className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-sand shadow-xl print:max-w-none print:max-h-none print:overflow-visible print:shadow-none print:border-0 print:rounded-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Screen-only controls */}
          <div className="sticky top-0 z-10 bg-white border-b border-sand px-5 py-3 flex items-center justify-between print:hidden">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5C5652] ornament-dot">Prescription slip · കുറിപ്പടി</div>
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                data-testid="rx-slip-print"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover"
              >
                <Printer className="h-4 w-4" strokeWidth={1.75} /> Print
              </button>
              <button
                onClick={onClose}
                data-testid="rx-slip-close"
                className="h-9 w-9 rounded-lg border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Printable body */}
          <div className="p-8 print:p-6 text-[#2A2624]" data-testid="rx-slip-body">
            {/* Letterhead */}
            <div className="border-b-2 border-forest pb-4 mb-4">
              <div className="flex items-center justify-center">
                <BrandLogo size="h-14" />
              </div>
              <div className="mt-3 text-xs text-[#5C5652] tracking-wider uppercase text-center">
                {branch?.name || ""} branch · Prescription slip · കുറിപ്പടി
              </div>
              {branch?.contact_line && (
                <div className="text-[11px] text-[#5C5652] mt-1 text-center" data-testid="rx-slip-contact">
                  {branch.contact_line}
                </div>
              )}
            </div>

            {/* Patient block */}
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Patient / രോഗിയുടെ പേര്</div>
                <div className="font-semibold">{patient.name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">MRN · Token</div>
                <div className="font-mono">{patient.mrn} · {patient.op_token}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Age · Gender</div>
                <div>{patient.age} · {patient.gender}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Date · തീയതി</div>
                <div>{date}</div>
              </div>
            </div>

            {/* Diagnosis */}
            {dx && (
              <div className="mb-4 p-3 rounded bg-[#F9F6F0] border border-sand text-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652] mb-1">
                  Diagnosis · രോഗനിർണ്ണയം
                </div>
                <div className="whitespace-pre-wrap">{dx}</div>
              </div>
            )}

            {/* Rx symbol + table */}
            <div className="flex items-baseline gap-2 mb-2 border-b border-sand pb-1">
              <span className="font-display text-3xl text-forest">℞</span>
              <span className="text-xs uppercase tracking-wider text-[#5C5652]">
                Medicines · മരുന്നുകൾ
              </span>
            </div>

            {rx.length === 0 ? (
              <div className="text-sm text-[#5C5652] italic py-4">No medicines prescribed.</div>
            ) : (
              <table className="w-full text-sm mb-6" data-testid="rx-slip-table">
                <thead>
                  <tr className="text-left border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652]">
                    <th className="py-2 pr-2 w-8">#</th>
                    <th className="py-2 pr-2">Medicine · മരുന്ന്</th>
                    <th className="py-2 pr-2">Dose · അളവ്</th>
                    <th className="py-2 pr-2">When · സേവനക്രമം</th>
                    <th className="py-2 pr-2">Duration · കാലാവധി</th>
                    <th className="py-2 pr-2 text-right">Qty · എണ്ണം</th>
                  </tr>
                </thead>
                <tbody>
                  {rx.map((it, i) => (
                    <tr key={i} className="border-b border-sand/60 align-top" data-testid={`rx-slip-row-${i}`}>
                      <td className="py-2 pr-2 font-mono">{i + 1}</td>
                      <td className="py-2 pr-2 font-semibold">{it.drug}</td>
                      <td className="py-2 pr-2">{it.dose || "—"}</td>
                      <td className="py-2 pr-2">{bilingualFreq(it.frequency)}</td>
                      <td className="py-2 pr-2">{it.duration || "—"}</td>
                      <td className="py-2 pr-2 text-right font-mono">{it.qty || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Instructions footer */}
            <div className="text-[11px] text-[#5C5652] border-t border-sand pt-3 mb-6">
              <div className="font-semibold text-[#2A2624]">നിർദ്ദേശം · Instructions</div>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>ഡോക്ടറുടെ നിർദ്ദേശപ്രകാരം മാത്രം മരുന്ന് കഴിക്കുക. · Take medicines only as directed.</li>
                <li>ചൂടുവെള്ളം ധാരാളം കുടിക്കുക, ശരീരവിശ്രമം എടുക്കുക. · Drink warm water and rest well.</li>
                <li>പാർശ്വഫലങ്ങൾ ഉണ്ടെങ്കിൽ ഉടൻ ഡോക്ടറെ ബന്ധപ്പെടുക. · Contact us if any side-effects appear.</li>
              </ul>
            </div>

            {/* Doctor signature */}
            <div className="flex justify-end">
              <div className="text-right text-sm">
                <div className="border-t border-forest pt-1 min-w-[220px] mt-8">
                  <div className="font-semibold">Dr. {doctor.replace(/^Dr\.?\s*/i, "")}</div>
                  <div className="text-[10px] text-[#5C5652] uppercase tracking-wider">Signature · ഒപ്പ്</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only overrides: hide app chrome, single page */}
      <style>{`
        @media print {
          body { background: white !important; }
          body > * { visibility: hidden !important; }
          [data-testid="rx-slip"], [data-testid="rx-slip"] * { visibility: visible !important; }
          [data-testid="rx-slip"] { position: absolute !important; inset: 0 !important; background: white !important; }
          @page { margin: 12mm; }
        }
      `}</style>
    </Portal>
  );
}
